import { TestParserOptions } from "../../options/testParserOptions.js";
import { TestCollection } from "../../parser/models/testCollection.js";
import { parserClassInstFields } from "../../parser/parseClassInstFields.js";
import { parseObjectFields } from "../../parser/parseObjectFields.js";
import { executePreProcessing } from "../executePreProcessing.js";

/**
 * Generates a child group name by appending the child name to the parent group name.
 * 
 * @param groupName - The parent group name.
 * @param name - The name of the child element.
 * @returns The combined child group name.
 */
export const getChildGroupName = (groupName: string, name: string): string => `${groupName}_${name}`;

/**
 * Parses and processes the fields of a child object within a test group.
 * 
 * @param childGroupName - The group name for the child object.
 * @param value - The child object to parse.
 * @param options - Parser options.
 * @returns A collection of tests extracted from the child object.
 */
export function processChildObjectFields(childGroupName: string, value: any, options: TestParserOptions): TestCollection {
  const childFields = parseObjectFields(childGroupName, value);

  return executePreProcessing(childFields, options);
}

/**
 * Extracts and processes tests from a class instance's members.
 * 
 * @param childGroupName - The group name for the class instance.
 * @param classInstance - The instance to extract members from.
 * @param options - Parser options.
 * @returns A collection of tests extracted from the class instance.
 */
export function getClassInstanceChildren(childGroupName: string, classInstance: any, options: TestParserOptions): TestCollection {
  defineClassContextGetter(childGroupName, classInstance, options);

  const childFields = parserClassInstFields(childGroupName, classInstance)

  return executePreProcessing(childFields, options);
}

/**
 * Defines a getter on a class instance that provides access to the group's test context.
 * 
 * @param groupName - The name of the test group.
 * @param thisArg - The class instance to define the property on.
 * @param options - Parser options containing the context property name and lookup function.
 */
export function defineClassContextGetter(groupName: string, thisArg: any, options: TestParserOptions): void {
  Object.defineProperty(
    thisArg,
    options.classContextPropertyName!,
    {
      configurable: true,
      get: function () {
        return options.testContextLookup(groupName)
      },
    }
  )
}
